import { X, Minus, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { useStore } from "../lib/store";
import DHLEstimator from "./DHLEstimator";
import UpsellSameShipping from "./UpsellSameShipping";

export default function CartDrawer() {
  const {
    cart, cartOpen, setCartOpen, updateQty, removeLine, subtotal, addToCart,
  } = useStore();

  const estimateItems = useMemo(
    () => cart.map((l) => ({ product_id: l.product_id, sku: l.sku, qty: l.qty })),
    [cart],
  );

  // For the upsell: use the first line's product smallest variant suggestion.
  const upsellLine = cart[0];

  return (
    <>
      {cartOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
          onClick={() => setCartOpen(false)}
          data-testid="cart-backdrop"
        />
      )}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#0A0E0A] border-l border-white/5 z-[70] transform transition-transform duration-300 ${cartOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
        aria-hidden={!cartOpen}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <h3 className="font-serif text-2xl">Your Cart</h3>
          <button
            onClick={() => setCartOpen(false)}
            className="p-2 text-[#A8A5A1] hover:text-white"
            data-testid="cart-close-btn"
            aria-label="close cart"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-col h-[calc(100%-73px)]">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {cart.length === 0 ? (
              <div className="text-center text-[#75726D] py-16 text-sm" data-testid="cart-empty">
                Your cart is empty.
                <div className="mt-6">
                  <Link to="/shop" onClick={() => setCartOpen(false)} className="btn-ghost inline-block">
                    Browse Products
                  </Link>
                </div>
              </div>
            ) : (
              cart.map((l) => (
                <div key={l.sku} className="flex gap-4 py-4 border-b border-white/5" data-testid={`cart-line-${l.sku}`}>
                  <img src={l.image} alt={l.name} className="w-20 h-20 object-cover" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium truncate">{l.name}</div>
                        <div className="text-xs text-[#75726D] mt-0.5">{l.weight} · ${l.price.toFixed(2)}</div>
                      </div>
                      <button
                        onClick={() => removeLine(l.sku)}
                        className="text-[#75726D] hover:text-[#FCA5A5] p-1"
                        data-testid={`remove-${l.sku}`}
                        aria-label="remove item"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/10">
                        <button
                          onClick={() => updateQty(l.sku, l.qty - 1)}
                          className="p-2 text-[#A8A5A1] hover:text-white"
                          data-testid={`dec-${l.sku}`}
                          aria-label="decrease"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm" data-testid={`qty-${l.sku}`}>{l.qty}</span>
                        <button
                          onClick={() => updateQty(l.sku, l.qty + 1)}
                          className="p-2 text-[#A8A5A1] hover:text-white"
                          data-testid={`inc-${l.sku}`}
                          aria-label="increase"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="text-sm font-medium">${(l.price * l.qty).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-white/5 p-6 space-y-4">
              <DHLEstimator items={estimateItems} compact />

              {/* Upsell 50g same-DHL nudge */}
              {upsellLine && (
                <UpsellSameShipping
                  smallestVariant={{ sku: `${upsellLine.sku.split("-")[0]}-50G`, weight: "50g", price: 15.99 }}
                  onAdd={() =>
                    addToCart(
                      { id: upsellLine.product_id, slug: upsellLine.slug, name: upsellLine.name, image: upsellLine.image },
                      { sku: `${upsellLine.sku.split("-")[0]}-50G`, weight: "50g", price: 15.99 },
                      1,
                    )
                  }
                />
              )}

              <div className="flex justify-between text-sm">
                <span className="text-[#A8A5A1]">Subtotal</span>
                <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-[11px] text-[#75726D] leading-relaxed">
                Duties &amp; local taxes are collected by customs on arrival.
              </p>
              <Link
                to="/checkout"
                onClick={() => setCartOpen(false)}
                className="btn-gold w-full"
                data-testid="cart-checkout-btn"
              >
                Proceed to Checkout
              </Link>
              <p className="text-[11px] text-[#75726D] text-center">Secure · Stripe · Bank · Ships worldwide via DHL</p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
